import type { FocusEventHandler, MouseEventHandler, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { getArticleSlugFromPath } from '@/utils/content';
import { useArticleReader } from '@/components/feature/ArticleReaderProvider';

type ArticleLinkProps = {
  to: string;
  className?: string;
  children: ReactNode;
  onMouseEnter?: MouseEventHandler<HTMLElement>;
  onMouseLeave?: MouseEventHandler<HTMLElement>;
  onFocus?: FocusEventHandler<HTMLElement>;
  onBlur?: FocusEventHandler<HTMLElement>;
  'aria-label'?: string;
};

/**
 * Drop-in replacement for react-router's <Link>: opening a known article renders a
 * button that shows the in-page reader modal, while any other route stays a normal link.
 */
export default function ArticleLink({
  to,
  className,
  children,
  onMouseEnter,
  onMouseLeave,
  onFocus,
  onBlur,
  'aria-label': ariaLabel,
}: ArticleLinkProps) {
  const { openArticle } = useArticleReader();
  const slug = getArticleSlugFromPath(to);

  if (slug) {
    // A <button> defaults to shrink-to-fit while a block <a> fills its container,
    // so we make the button fill the width unless the caller asked for an inline pill.
    const isInlineWidth = typeof className === 'string' && /(inline-flex|w-fit)/.test(className);
    return (
      <button
        type="button"
        onClick={() => openArticle(slug)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
        aria-label={ariaLabel}
        className={[isInlineWidth ? '' : 'w-full', 'cursor-pointer text-left', className].filter(Boolean).join(' ')}
      >
        {children}
      </button>
    );
  }

  return (
    <Link
      to={to}
      className={className}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      aria-label={ariaLabel}
    >
      {children}
    </Link>
  );
}